import dayjs from 'dayjs';
import 'dayjs/locale/it';
import {
  EccezioneNegozio,
  ExtraCalendarUserInterface,
} from '../../../interfaces/ExtraCalendarUser';
import { UserInterface } from '../../../interfaces/User';
import { formatFasceOrarie } from './FasceOrarieUtils';

/**
 * Assenze degli operatori mostrate nella fascia Presenze dell'agenda negozio.
 *
 * Le righe arrivano da ExtraCalendarUser (le stesse dei turni): il tipo dice se è
 * permesso, malattia, ferie o un'assenza generica ("Altro"), mentre gli orari della
 * riga sono quelli in cui l'operatore è presente quel giorno (il motore degli slot
 * li usa così), e `isClosingDay` vuol dire assente tutto il giorno.
 */

export type TipoAssenza = 'Ferie' | 'Permesso' | 'Malattia' | 'Altro';

export interface AssenzaOperatore {
  key: string;
  userId: string;
  username: string;
  color: string;
  tipo: TipoAssenza;
  /** YYYY-MM-DD */
  dal: string;
  /** YYYY-MM-DD */
  al: string;
  /** Fasce di presenza già formattate; null = assente tutto il giorno. */
  orario: string | null;
  /** L'assenza arriva al bordo della finestra letta: può iniziare prima di `dal`. */
  iniziaPrima: boolean;
  /** L'assenza arriva al bordo della finestra letta: può finire dopo `al`. */
  continuaDopo: boolean;
}

export interface FinestraAssenze {
  /** YYYY-MM-DD */
  inizio: string;
  /** YYYY-MM-DD */
  fine: string;
}

const TIPI_ASSENZA: Record<number, TipoAssenza> = {
  1: 'Permesso',
  2: 'Malattia',
  3: 'Ferie',
};

const FORMATO_DATA = 'YYYY-MM-DD';

// I turni e la configurazione utente salvano un record per giorno: per mostrare
// dove inizia e finisce davvero un'assenza a cavallo dei giorni visibili si leggono
// anche i giorni intorno. Oltre il margine l'assenza si dichiara aperta, senza
// inventare date.
const MARGINE_PRIMA = 31;
// Dal lunedì: copre la domenica della settimana, i 3 Giorni che sconfinano nella
// settimana dopo (al massimo fino a martedì) e lo stesso margine di 31 giorni.
const MARGINE_DOPO = 45;

/**
 * Intervallo da chiedere al backend, ancorato al lunedì della settimana del primo
 * giorno visibile: Giorno, 3 Giorni, Settimana e Agenda della stessa settimana
 * condividono la chiave della query, e spostandosi di un giorno non si rilegge
 * nulla. Il filtro per giorno si fa poi sui dati già letti.
 */
export const getFinestraAssenze = (primoGiorno: string): FinestraAssenze => {
  const lunedi = dayjs(primoGiorno).locale('it').startOf('week');
  return {
    inizio: lunedi.subtract(MARGINE_PRIMA, 'day').format(FORMATO_DATA),
    fine: lunedi.add(MARGINE_DOPO, 'day').format(FORMATO_DATA),
  };
};

// Ogni tipo diverso da permesso, malattia e ferie è un'assenza generica, come
// l'"Altro" dei turni.
const getTipoAssenza = (riga: ExtraCalendarUserInterface): TipoAssenza =>
  TIPI_ASSENZA[riga.extraCalendarType] ?? 'Altro';

// Una riga "Altro" (tipo 0 o qualsiasi tipo diverso da 1, 2 e 3) è un'assenza solo
// se dura tutto il giorno: chiusura, oppure nessuna fascia valida (es. 00:00-00:00),
// e per il backend quel giorno la persona non lavora. Con almeno una fascia valida è
// un cambio d'orario: la persona è in servizio e i suoi orari li porta già la
// chiamata "in servizio". Così anche i turni importati da Factorial (una riga tipo 0
// per ogni giorno lavorato) non riempiono la fascia di assenze finte.
const isCambioOrario = (riga: ExtraCalendarUserInterface): boolean =>
  !riga.isClosingDay &&
  !(riga.extraCalendarType in TIPI_ASSENZA) &&
  formatFasceOrarie(riga) !== null;

const getOrario = (riga: ExtraCalendarUserInterface): string | null =>
  riga.isClosingDay ? null : formatFasceOrarie(riga);

const getData = (data: string): string => data.slice(0, 10);

// Il backend copia ogni chiusura o apertura straordinaria del negozio, campo per
// campo, su tutti gli utenti che ne seguono l'orario: quelle copie non sono
// assenze dell'utente e si riconoscono perché coincidono con l'eccezione del negozio.
const getChiaveEccezione = (riga: EccezioneNegozio): string =>
  [
    getData(riga.startValidDate),
    getData(riga.endValidDate),
    riga.isClosingDay,
    riga.startTime1 ?? '',
    riga.endTime1 ?? '',
    riga.startTime2 ?? '',
    riga.endTime2 ?? '',
    riga.extraCalendarType,
  ].join('|');

// Vero se il periodo che inizia in `dal` si sovrappone o segue senza buchi quello
// che finisce in `al`.
const isContiguo = (al: string, dal: string): boolean =>
  !dayjs(dal).isAfter(dayjs(al).add(1, 'day'), 'day');

/**
 * Trasforma le righe ExtraCalendarUser del punto vendita nell'elenco delle assenze
 * lette nella finestra. Le righe dello stesso operatore con lo stesso tipo e lo
 * stesso orario su giorni consecutivi diventano un'unica voce, che mostra l'assenza
 * per intero entro la finestra letta (`finestra`, vedi getFinestraAssenze). Il
 * filtro sui giorni visibili lo fa chi usa l'elenco (buildPresenzeGiorni).
 */
export const buildAssenzeOperatori = (
  righe: ExtraCalendarUserInterface[],
  eccezioniNegozio: EccezioneNegozio[],
  users: Record<string, UserInterface>,
  pointOfSaleId: string,
  finestra: FinestraAssenze
): AssenzaOperatore[] => {
  const copieNegozio = new Set(eccezioniNegozio.map(getChiaveEccezione));
  const assenze: AssenzaOperatore[] = [];
  for (const riga of righe) {
    // Difesa: tiene solo le righe del punto vendita richiesto.
    if (riga.pointOfSaleId !== pointOfSaleId) continue;
    if (copieNegozio.has(getChiaveEccezione(riga))) continue;
    if (isCambioOrario(riga)) continue;
    // Stessi utenti dei turni e della fascia: le righe restano salvate anche
    // dopo che un utente viene disattivato o tolto dall'agenda.
    const user = users[riga.userId];
    if (!user || !user.isActive || !user.hasAgenda) continue;
    assenze.push({
      key: riga.id,
      userId: riga.userId,
      username: user.username,
      color: user.color,
      tipo: getTipoAssenza(riga),
      dal: getData(riga.startValidDate),
      al: getData(riga.endValidDate),
      orario: getOrario(riga),
      iniziaPrima: false,
      continuaDopo: false,
    });
  }

  assenze.sort(
    (a, b) =>
      a.userId.localeCompare(b.userId) ||
      a.tipo.localeCompare(b.tipo) ||
      (a.orario ?? '').localeCompare(b.orario ?? '') ||
      a.dal.localeCompare(b.dal)
  );

  const unite: AssenzaOperatore[] = [];
  for (const assenza of assenze) {
    const ultima = unite[unite.length - 1];
    if (
      ultima &&
      ultima.userId === assenza.userId &&
      ultima.tipo === assenza.tipo &&
      ultima.orario === assenza.orario &&
      isContiguo(ultima.al, assenza.dal)
    ) {
      if (assenza.al > ultima.al) ultima.al = assenza.al;
      continue;
    }
    unite.push({ ...assenza });
  }

  return unite
    .map((a) => ({
      ...a,
      iniziaPrima: a.dal <= finestra.inizio,
      continuaDopo: a.al >= finestra.fine,
    }))
    .sort(
      (a, b) =>
        a.dal.localeCompare(b.dal) || a.username.localeCompare(b.username)
    );
};

const formatData = (data: string): string => dayjs(data).format('DD/MM');

/**
 * Periodo dell'assenza nel popover della fascia, con l'iniziale maiuscola come le
 * altre righe di dettaglio: «Dal 16/10 al 20/10», «Fino al …», «Dal …»,
 * «Assenza prolungata». Un'assenza di un giorno solo restituisce '': la data è
 * già nel titolo del popover. Stessi testi di FOCUS CLOUD.
 */
export const formatPeriodoAssenza = ({
  dal,
  al,
  iniziaPrima,
  continuaDopo,
}: Pick<
  AssenzaOperatore,
  'dal' | 'al' | 'iniziaPrima' | 'continuaDopo'
>): string => {
  if (iniziaPrima && continuaDopo) return 'Assenza prolungata';
  if (iniziaPrima) return `Fino al ${formatData(al)}`;
  if (continuaDopo) return `Dal ${formatData(dal)}`;
  if (dal === al) return '';
  return `Dal ${formatData(dal)} al ${formatData(al)}`;
};

/**
 * Dettaglio di una voce degli assenti nel popover: il periodo e, per chi è in
 * servizio solo in parte, l'orario di presenza (`orario` porta le fasce in cui la
 * persona c'è, non quelle dell'assenza), es. «Dal 16/10 al 20/10 · in servizio
 * 09:00-13:00». Per un giorno solo il periodo non c'è (la data è nel titolo):
 * «Tutto il giorno» oppure «In servizio 09:00-13:00». Stesso testo di FOCUS CLOUD.
 */
export const formatDettaglioAssenza = (
  assenza: Pick<
    AssenzaOperatore,
    'dal' | 'al' | 'iniziaPrima' | 'continuaDopo' | 'orario'
  >
): string => {
  const periodo = formatPeriodoAssenza(assenza);
  if (assenza.orario === null) return periodo || 'Tutto il giorno';
  return periodo
    ? `${periodo} · in servizio ${assenza.orario}`
    : `In servizio ${assenza.orario}`;
};
